import React from 'react';
import useIsMobile from 'Hooks/useIsMobile';
import { Svg } from './CaseListUI';

// ---------------------------------------------------------------------------
// Shared pieces for the team-up screens (Team up with funders popup, Funder
// Teamup dashboard): one row per funder / attorney, section labels, pills.
// ---------------------------------------------------------------------------

export const TU_ICON = {
  plus: ['M16 21v-2a4 4 0 0 0-8 0v2', 'M12 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z', 'M19 8v6M22 11h-6'],
  clock: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z', 'M12 7v5l3 2'],
  link: ['M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7', 'M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7'],
  check: 'M5 12l5 5 9-10',
  mail: ['M3 5h18v14H3z', 'M3 6l9 7 9-7'],
  phone: 'M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2',
  x: 'M6 6l12 12M18 6L6 18',
  funder: ['M12 3v18', 'M17 7.5c0-1.9-2.2-3.5-5-3.5s-5 1.6-5 3.5S9.2 11 12 11s5 1.6 5 3.5-2.2 3.5-5 3.5-5-1.6-5-3.5'],
};

export const initialsOf = (u) => (((u?.firstName || u?.company || '')[0] || '') + ((u?.lastName || '')[0] || '')).toUpperCase() || '·';
export const hrefOf = (link) => (!link ? null : /^https?:\/\//i.test(link) ? link : 'https://' + link);

// Known avatar sizes -> full class strings (Tailwind must see literal names).
// Any other size falls back to inline width/height/font-size.
const AVATAR_SIZES = {
  36: { box: 'w-9 h-9', text: 'text-[13px]' },
  38: { box: 'w-[38px] h-[38px]', text: 'text-[14px]' },
  48: { box: 'w-12 h-12', text: 'text-[17px]' },
};

export function Avatar({ user, size = 36 }) {
  const known = AVATAR_SIZES[size];
  const base = `${known ? known.box + ' ' : ''}flex-none rounded-full flex items-center justify-center overflow-hidden`;
  const boxStyle = known ? undefined : { width: size + 'px', height: size + 'px' };
  if (user?.isUserFunder) {
    return <span className={`${base} bg-brass/[.12] text-brass`} style={boxStyle}><Svg d={TU_ICON.funder} size={Math.round(size * 0.5)} width={2} /></span>;
  }
  if (user?.profilePic) return <img src={user.profilePic} alt="" className={`${base} object-cover border border-navy/[.1]`} style={boxStyle} />;
  return (
    <span className={`${base} bg-gold text-navy font-plexsans font-semibold leading-none ${known ? known.text : ''}`} style={known ? undefined : { ...boxStyle, fontSize: Math.round(size * 0.36) + 'px' }}>
      {initialsOf(user)}
    </span>
  );
}

export function TuSection({ label, count, children }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="font-plexsans font-semibold text-[11px] leading-none tracking-[.08em] uppercase text-navy/[.55]">{label}</span>
        {count != null && <span className="font-plexsans font-semibold text-[11px] leading-none text-navy bg-navy/[.07] rounded-[10px] py-[3px] px-[7px]">{count}</span>}
      </div>
      <div className="bg-white border border-navy/[.12] rounded-md overflow-hidden">{children}</div>
    </div>
  );
}

const TU_PILL_TONES = {
  green: 'text-success bg-success/[.08] border-success/[.35]',
  navy: 'text-navy bg-navy/[.05] border-navy/[.18]',
  brass: 'text-brass bg-brass/[.08] border-brass/[.3]',
};

export function TuPill({ tone = 'brass', icon, children }) {
  const t = tone === 'green' ? TU_PILL_TONES.green : tone === 'navy' ? TU_PILL_TONES.navy : TU_PILL_TONES.brass;
  return (
    <span className={`inline-flex items-center gap-1.5 font-plexsans font-semibold text-[12px] leading-none border rounded-[20px] py-1.5 px-[11px] whitespace-nowrap ${t}`}>
      {icon && <Svg d={icon} size={12} width={2.2} />}{children}
    </span>
  );
}

// One funder / attorney. `right` holds the status / action for this row.
export function PersonRow({ user, onDetails, detailsLabel = 'View details', right, extra }) {
  const isMobile = useIsMobile();
  const link = hrefOf(user?.lawFirmLink);
  const name = user?.isUserFunder ? (user?.company || `${user?.firstName || ''} ${user?.lastName || ''}`) : `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
  const sub = user?.isUserFunder ? `${user?.firstName || ''} ${user?.lastName || ''}`.trim() : user?.company;
  return (
    <div className={`flex border-b border-navy/[.08] ${isMobile ? 'flex-col items-stretch gap-3 p-3.5' : 'flex-row items-center gap-4 py-3.5 px-[18px]'}`}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <Avatar user={user} />
        <div className="flex flex-col gap-[3px] min-w-0">
          <span onClick={onDetails} className={`font-plexsans font-semibold text-[14px] leading-[1.3] text-navy overflow-hidden text-ellipsis whitespace-nowrap ${onDetails ? 'cursor-pointer' : 'cursor-default'}`}>{name || '—'}</span>
          <span className="flex flex-wrap gap-y-1 gap-x-2.5 font-plexsans font-normal text-[12.5px] leading-[1.3] text-navy/[.55]">
            {sub && <span>{sub}</span>}
            {link && <a href={link} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-brass underline [overflow-wrap:anywhere]">{user.lawFirmLink}</a>}
          </span>
          {extra}
        </div>
      </div>
      <div className={`flex items-center gap-3 flex-wrap ${isMobile ? 'justify-between' : 'justify-end'}`}>
        {onDetails && <span onClick={onDetails} className="font-plexsans font-semibold text-[12.5px] leading-none text-navy underline cursor-pointer whitespace-nowrap">{detailsLabel}</span>}
        {right}
      </div>
    </div>
  );
}

// `style` is still accepted (applied inline, so it wins over the classes);
// `className` is appended after the built-in classes.
export function TuButton({ onClick, children, icon, variant = 'primary', disabled, style, className }) {
  // Non-primary variants had `hover:opacity-90`, but the inline `opacity` always
  // overrode it, so it never showed - left out to keep the look unchanged.
  const v = variant === 'primary'
    ? 'text-ivory bg-brass border-0 hover:bg-brassDark transition-colors'
    : variant === 'danger'
      ? 'text-ivory bg-danger border-0'
      : 'text-navy bg-white border border-navy/[.25]';
  return (
    <button type="button" disabled={disabled} onClick={(e) => { e.stopPropagation(); if (!disabled && onClick) onClick(e); }}
      className={`inline-flex items-center justify-center gap-[7px] font-plexsans font-semibold text-[13px] leading-none rounded-md py-2.5 px-4 whitespace-nowrap ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer opacity-100'} ${v}${className ? ' ' + className : ''}`}
      style={style}>
      {icon && <Svg d={icon} size={14} width={2.1} />}{children}
    </button>
  );
}
